import { useCallback } from "react";
import { useWatch } from "react-hook-form";

import {
  CaseTemplateFormValues,
  WorkflowRuleTaskWithId,
  WorkflowRuleActionWithId,
} from "src/cases/templates/caseTemplateFormUtils";
import { generateInternalId } from "src/cases/templates/internalIdUtils";

/**
 * Hook to manage internal ID-based tracking for tasks and actions
 */
export const useIdKeyMapping = () => {
  const tasks = useWatch<CaseTemplateFormValues, "attributes.case_tasks">({
    name: "attributes.case_tasks",
  });

  const actions = useWatch<CaseTemplateFormValues, "attributes.case_actions">({
    name: "attributes.case_actions",
  });

  const createWorkflowTask = useCallback(
    (taskKey: string) => {
      const task = tasks?.find((t) => t.key === taskKey);
      return {
        task_key: taskKey,
        _internalId: task?._internalId || generateInternalId(),
      };
    },
    [tasks],
  );

  const createWorkflowAction = useCallback(
    (actionKey: string) => {
      const action = actions?.find((a) => a.key === actionKey);
      return {
        action_key: actionKey,
        _internalId: action?._internalId || generateInternalId(),
        automation: [],
        required_tasks_keys: [],
      };
    },
    [actions],
  );

  const createRequiredTask = useCallback(
    (taskKey: string) => {
      const task = tasks?.find((t) => t.key === taskKey);
      return {
        task_key: taskKey,
        _internalId: task?._internalId || generateInternalId(),
      };
    },
    [tasks],
  );

  const isSameTask = useCallback(
    (task1: WorkflowRuleTaskWithId, task2: WorkflowRuleTaskWithId) => {
      return task1._internalId === task2._internalId;
    },
    [],
  );

  const isSameAction = useCallback(
    (action1: WorkflowRuleActionWithId, action2: WorkflowRuleActionWithId) => {
      return action1._internalId === action2._internalId;
    },
    [],
  );

  return {
    createWorkflowTask,
    createWorkflowAction,
    createRequiredTask,
    isSameTask,
    isSameAction,
  };
};
